import { z } from "zod";

import { hashString } from "@/lib/hash";
import { readStored, writeStored } from "@/lib/storage";
import type { ModuleId } from "@/router/routes";

import { FIRST_SAMPLE_ID } from "../samples";
import { projectType } from "../taxonomy";
import { withStudio, type StudioSummary } from "../projects/studio-status";
import type { ModuleStatus, NewProjectInput, ProjectPatch, ProjectSummary } from "../types";
import { FIXTURES, type ProjectFixture } from "./fixtures";
import { LATERITE_FIXTURES } from "./fixtures-laterite";
import { DEMO_ORGS } from "./organisations";

/**
 * Projects per organisation for the mock backend: fixture portfolios for the demo organisations, projects people
 * create, archive flags, per-person pins and read notifications. Organisations created in the staff console start
 * empty until someone adds the sample project (FR-PROJ-009).
 */

export const PROJECTS_STORAGE_KEY = "orthos.mock.v2";
const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 86_400_000;
const PROJECT_YEAR = new Date().getFullYear();
/** Digits in the running number of a project ID, e.g. NL-2026-014. */
const PROJECT_NUMBER_DIGITS = 3;
const SAMPLE_FIXTURE_ID = "sample-villa";

const PORTFOLIOS: Readonly<Record<string, readonly ProjectFixture[]>> = {
  [DEMO_ORGS.northlight]: FIXTURES,
  [DEMO_ORGS.laterite]: LATERITE_FIXTURES,
};

const newProjectSchema = z.object({
  name: z.string().min(1),
  client: z.string(),
  type: z.string().min(1),
  city: z.string(),
  state: z.string(),
  address: z.string().optional(),
  templateId: z.string(),
  team: z.array(z.string()),
  invites: z.array(z.string()).optional(),
  boundary: z
    .discriminatedUnion("kind", [
      z.object({
        kind: z.literal("rectangle"),
        widthM: z.number().positive(),
        depthM: z.number().positive(),
      }),
      z.object({ kind: z.literal("later") }),
    ])
    .optional(),
  location: z.object({ lat: z.number(), lon: z.number() }).optional(),
});

const editSchema = z.object({
  name: z.string().min(1).optional(),
  client: z.string().optional(),
  type: z.string().min(1).optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  address: z.string().optional(),
});

export const storedSchema = z.object({
  created: z.array(
    z.object({
      id: z.string(),
      organisationId: z.string(),
      shortId: z.string(),
      createdAt: z.string(),
      key: z.string(),
      input: newProjectSchema,
    }),
  ),
  /** Whether each organisation shows the sample project; demo portfolios show theirs unless removed. */
  samples: z.record(z.string(), z.boolean()),
  archived: z.record(z.string(), z.boolean()),
  pins: z.record(z.string(), z.record(z.string(), z.boolean())),
  read: z.record(z.string(), z.record(z.string(), z.boolean())),
  /** Renames and taxonomy changes per project (WS-29); absent in what older builds stored. */
  edits: z.record(z.string(), editSchema).default({}),
});

type Stored = z.infer<typeof storedSchema>;
type Extras = { address?: string; plotAreaM2?: number };

/** Render has no status until renders exist (the module is not built), so the fixtures' invented counts are left out. */
function moduleStatuses(entries: ProjectFixture["modules"]): ModuleStatus[] {
  return (Object.entries(entries) as [ModuleId, [string, ModuleStatus["tone"]]][])
    .filter(([module]) => module !== "render")
    .map(([module, [label, tone]]) => ({ module, label, tone }));
}

function shortId(prefix: string, number: number): string {
  return `${prefix}-${String(PROJECT_YEAR)}-${String(number).padStart(PROJECT_NUMBER_DIGITS, "0")}`;
}

function fromFixture(
  fixture: ProjectFixture,
  index: number,
  now: number,
  prefix: string,
  id: string,
  team: string[],
): ProjectSummary {
  return {
    id,
    shortId: shortId(prefix, index + 1),
    name: fixture.name,
    client: fixture.client,
    discipline: projectType(fixture.type).discipline,
    type: fixture.type,
    city: fixture.city,
    state: fixture.state,
    stageIndex: fixture.stageIndex,
    health: fixture.health,
    ...(fixture.next === undefined
      ? {}
      : {
          nextMilestone: {
            title: fixture.next.title,
            due: new Date(now + fixture.next.inDays * MS_PER_DAY).toISOString(),
          },
        }),
    team,
    updatedAt: new Date(now - fixture.updatedMinsAgo * MS_PER_MINUTE).toISOString(),
    pinned: fixture.pinned ?? false,
    archived: fixture.archived ?? false,
    sample: fixture.sample ?? false,
    ...(fixture.sample === true ? { sampleId: FIRST_SAMPLE_ID } : {}),
    attention: fixture.attention,
    modules: moduleStatuses(fixture.modules),
    coverSeed: hashString(id),
  };
}

function fromCreated(entry: Stored["created"][number]): ProjectSummary {
  const type = projectType(entry.input.type);
  const hasSite = type.discipline !== "I" && type.discipline !== "E";
  return {
    id: entry.id,
    shortId: entry.shortId,
    name: entry.input.name,
    client: entry.input.client,
    discipline: type.discipline,
    type: type.code,
    city: entry.input.city,
    state: entry.input.state,
    stageIndex: 0,
    health: { state: "no_schedule" },
    team: entry.input.team,
    updatedAt: entry.createdAt,
    pinned: false,
    archived: false,
    sample: false,
    attention: 0,
    modules: [
      ...(hasSite
        ? [
            {
              module: "site" as const,
              label: entry.input.location === undefined ? "Add location" : "Location set",
              tone: "neutral" as const,
            },
          ]
        : []),
      { module: "design", label: "Not started", tone: "neutral" },
      { module: "studio", label: "Set up", tone: "neutral" },
    ],
    coverSeed: hashString(entry.id),
  };
}

function slug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

const SAMPLE = FIXTURES.find((fixture) => fixture.id === SAMPLE_FIXTURE_ID);

export interface ProjectStore {
  /** An organisation's projects before per-person visibility; `viewer` fills the sample's team in new organisations. */
  list(organisationId: string, prefix: string, viewer: string): ProjectSummary[];
  /** Which organisation holds a project (for access checks and request names). */
  locate(projectId: string): { organisationId: string; name: string } | undefined;
  extras(projectId: string): Extras;
  allExtras(): ReadonlyMap<string, Extras>;
  create(
    organisationId: string,
    prefix: string,
    viewer: string,
    input: NewProjectInput,
    key: string,
  ): string;
  setSample(organisationId: string, shown: boolean): void;
  /** Renames a project or changes its taxonomy fields, in this browser. */
  edit(projectId: string, patch: ProjectPatch): void;
  setArchived(projectId: string, archived: boolean): void;
  setPinned(userId: string, projectId: string, pinned: boolean): void;
  pinOverride(userId: string, projectId: string): boolean | undefined;
  readOverride(userId: string, notificationId: string): boolean | undefined;
  setRead(userId: string, notificationId: string, read: boolean): void;
  /** The project's stage and Studio status from the mock's Studio, which keeps nothing across a reload either. */
  setStudio(projectId: string, summary: StudioSummary): void;
}

export function createProjectStore(now: number): ProjectStore {
  const stored: Stored = readStored(PROJECTS_STORAGE_KEY, storedSchema) ?? {
    created: [],
    samples: {},
    archived: {},
    pins: {},
    read: {},
    edits: {},
  };
  const save = () => {
    writeStored(PROJECTS_STORAGE_KEY, stored);
  };
  const studio = new Map<string, StudioSummary>();
  const extras = new Map<string, Extras>(
    Object.values(PORTFOLIOS)
      .flat()
      .map((f) => [
        f.id,
        {
          ...(f.address === undefined ? {} : { address: f.address }),
          ...(f.plotAreaM2 === undefined ? {} : { plotAreaM2: f.plotAreaM2 }),
        },
      ]),
  );
  const sampleId = (organisationId: string) =>
    organisationId === DEMO_ORGS.northlight
      ? SAMPLE_FIXTURE_ID
      : `${SAMPLE_FIXTURE_ID}-${organisationId.replace(/^org-/, "")}`;
  const sampleShown = (organisationId: string) =>
    stored.samples[organisationId] ?? PORTFOLIOS[organisationId] !== undefined;

  /** A project with its archive flag and the changes made to it here. */
  const edited = (project: ProjectSummary): ProjectSummary => {
    const edit = stored.edits[project.id] ?? {};
    const type = edit.type ?? project.type;
    return {
      ...project,
      name: edit.name ?? project.name,
      client: edit.client ?? project.client,
      type,
      discipline: projectType(type).discipline,
      city: edit.city ?? project.city,
      state: edit.state ?? project.state,
      archived: stored.archived[project.id] ?? project.archived,
    };
  };
  const withOwnStudio = (project: ProjectSummary): ProjectSummary => {
    const summary = studio.get(project.id);
    return summary === undefined ? project : withStudio(project, summary);
  };

  const list = (organisationId: string, prefix: string, viewer: string): ProjectSummary[] => {
    const fixtures = (PORTFOLIOS[organisationId] ?? []).filter((f) => f.id !== SAMPLE_FIXTURE_ID);
    const fromFixtures = fixtures.map((f, index) =>
      fromFixture(f, index, now, prefix, f.id, f.team),
    );
    const sample =
      SAMPLE !== undefined && sampleShown(organisationId)
        ? [
            fromFixture(
              SAMPLE,
              fixtures.length,
              now,
              prefix,
              sampleId(organisationId),
              organisationId === DEMO_ORGS.northlight ? SAMPLE.team : [viewer],
            ),
          ]
        : [];
    const created = stored.created
      .filter((entry) => entry.organisationId === organisationId)
      .map(fromCreated);
    return [...created, ...fromFixtures, ...sample].map((project) =>
      withOwnStudio(edited(project)),
    );
  };

  const locate = (projectId: string) => {
    for (const [organisationId, fixtures] of Object.entries(PORTFOLIOS)) {
      const fixture = fixtures.find((f) => f.id === projectId);
      if (fixture !== undefined) return { organisationId, name: fixture.name };
    }
    const created = stored.created.find((entry) => entry.id === projectId);
    if (created !== undefined)
      return { organisationId: created.organisationId, name: created.input.name };
    if (SAMPLE !== undefined && projectId.startsWith(`${SAMPLE_FIXTURE_ID}-`)) {
      return {
        organisationId: `org-${projectId.slice(SAMPLE_FIXTURE_ID.length + 1)}`,
        name: SAMPLE.name,
      };
    }
    return undefined;
  };

  return {
    list,
    locate,
    extras: (projectId) => {
      const base = extras.get(projectId) ?? extras.get(SAMPLE_FIXTURE_ID) ?? {};
      const address = stored.edits[projectId]?.address;
      return address === undefined ? base : { ...base, address };
    },
    allExtras: () => extras,
    create(organisationId, prefix, viewer, input, key) {
      const existing = stored.created.find((entry) => entry.key === key);
      if (existing !== undefined) return existing.id;
      const base = slug(input.name) || "project";
      let id = base;
      for (let n = 2; locate(id) !== undefined; n++) id = `${base}-${String(n)}`;
      const number = list(organisationId, prefix, viewer).length + 1;
      stored.created.unshift({
        id,
        organisationId,
        shortId: shortId(prefix, number),
        createdAt: new Date().toISOString(),
        key,
        input: newProjectSchema.parse(input),
      });
      save();
      return id;
    },
    setSample(organisationId, shown) {
      stored.samples[organisationId] = shown;
      save();
    },
    edit(projectId, patch) {
      stored.edits[projectId] = { ...stored.edits[projectId], ...editSchema.parse(patch) };
      save();
    },
    setArchived(projectId, archived) {
      stored.archived[projectId] = archived;
      save();
    },
    setPinned(userId, projectId, pinned) {
      stored.pins[userId] = { ...stored.pins[userId], [projectId]: pinned };
      save();
    },
    pinOverride: (userId, projectId) => stored.pins[userId]?.[projectId],
    readOverride: (userId, notificationId) => stored.read[userId]?.[notificationId],
    setRead(userId, notificationId, read) {
      stored.read[userId] = { ...stored.read[userId], [notificationId]: read };
      save();
    },
    setStudio(projectId, summary) {
      studio.set(projectId, summary);
    },
  };
}
